/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
"use client";
import { usePathname, useRouter } from "next/navigation";
import React, { useEffect, useState } from "react";

import GalleryImage from "@/components/carousel/gallery-image";
import GalleryLabel from "@/components/carousel/gallery-label";
import NavigationButton from "@/components/carousel/gallery-navigation-button";
import {
  type GalleryImage as GalleryImageType,
  type GalleryLabel as GalleryLabelType,
} from "@/types/gallery";

interface GalleryProps {
  currentImage: GalleryImageType;
  currentLabel: GalleryLabelType;
  currentSlug: string;
  allWorks: Array<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [x: string]: any;
    slug: string;
    title: string;
  }>;
  isFilteredGallery: boolean; // Flag to indicate if it's a filtered gallery
}

const Gallery: React.FC<GalleryProps> = ({
  currentImage,
  currentLabel,
  currentSlug,
  allWorks,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isFilteredGallery = true,
}): React.JSX.Element => {
  const router = useRouter();
  const pathname = usePathname(); // Get the current URL path
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const index = allWorks.findIndex((work) => work.slug === currentSlug);
    setCurrentIndex(index);
  }, [currentSlug, allWorks]);

  const navigate = (direction: "back" | "forward"): void => {
    const newIndex = direction === "back" ? currentIndex - 1 : currentIndex + 1;

    // If navigating backward on the first item, redirect to menu
    if (direction === "back" && currentIndex === 0) {
      router.push("/menu");
      return;
    }

    // If navigating forward on the last item and it's a filtered gallery, redirect to menu
    // if (direction === "forward" && currentIndex === allWorks.length - 1 && isFilteredGallery) {
    //   router.push("/menu");
    //   return;
    // }

    if (direction === "forward" && currentIndex === allWorks.length - 1) {
      router.push("/menu");
      return;
    }

    // Ensure newIndex is within bounds
    const nextWork = allWorks[newIndex];
    if (nextWork && typeof nextWork.slug === "string") {
      // If the gallery is filtered (i.e., the URL contains a specific category like /gallery/paintings)
      if (
        pathname.startsWith("/gallery/paintings") ||
        pathname.startsWith("/gallery/sculptures") ||
        pathname.startsWith("/gallery/earlier-works")
      ) {
        // Extract the category from the pathname, e.g., "/gallery/paintings"
        const categoryPath = pathname.split("/").slice(0, 3).join("/"); // This will preserve /gallery/paintings, /gallery/sculptures, etc.
        router.push(`${categoryPath}/${nextWork.slug}`);
      } else {
        // For unfiltered gallery, use just /gallery/[slug]
        const basePath = "/gallery"; // For non-filtered galleries
        router.push(`${basePath}/${nextWork.slug}`);
      }
    }
  };

  // Preload next and previous images to optimize navigation
  useEffect(() => {
    const preloadImages = (): void => {
      const prevIndex = currentIndex - 1;
      const nextIndex = currentIndex + 1;

      console.log("Gallery: Preloading adjacent images...");
      console.log(
        `Gallery: Current index = ${currentIndex}, Prev index = ${prevIndex}, Next index = ${nextIndex}`,
      );

      // Preload previous image if available
      if (prevIndex >= 0) {
        const prevWork = allWorks[prevIndex];
        if (prevWork?.image?.src) {
          console.log(
            `Gallery: Preloading previous image at index ${prevIndex}, image URL: ${prevWork.image.src}`,
          );
          const prevImg = new Image();
          prevImg.src = prevWork.image.src;
          prevImg.onload = (): void => {
            console.log(`Gallery: Successfully preloaded previous image: ${prevWork.image.src}`);
          };
          prevImg.onerror = (err): void => {
            console.error(`Gallery: Error preloading previous image: ${prevWork.image.src}`, err);
          };
        } else {
          console.log(`Gallery: No image available for previous item at index ${prevIndex}`);
        }
      } else {
        console.log("Gallery: No previous image to preload.");
      }

      // Preload next image if available
      if (nextIndex < allWorks.length) {
        const nextWork = allWorks[nextIndex];
        if (nextWork?.image?.src) {
          console.log(
            `Gallery: Preloading next image at index ${nextIndex}, image URL: ${nextWork.image.src}`,
          );
          const nextImg = new Image();
          nextImg.src = nextWork.image.src;
          nextImg.onload = (): void => {
            console.log(`Gallery: Successfully preloaded next image: ${nextWork.image.src}`);
          };
          nextImg.onerror = (err): void => {
            console.error(`Gallery: Error preloading next image: ${nextWork.image.src}`, err);
          };
        } else {
          console.log(`Gallery: No image available for next item at index ${nextIndex}`);
        }
      } else {
        console.log("Gallery: No next image to preload.");
      }
    };

    preloadImages();
  }, [currentIndex, allWorks]);

  return (
    <main className="gallery-content">
      <div className="gallery-content-viewer">
        <div className="gallery-content-viewer-cell gallery-content-viewer-navigation">
          <NavigationButton direction="back" onNavigate={() => navigate("back")} />
        </div>

        <div className="gallery-content-viewer-cell gallery-content-viewer-media">
          <GalleryImage {...currentImage} />
          <div className="gallery-media-loading-indicator"></div>
        </div>

        <div className="gallery-content-viewer-cell gallery-content-viewer-navigation">
          <NavigationButton direction="forward" onNavigate={() => navigate("forward")} />
        </div>
        <div className="gallery-content-viewer-cell gallery-content-viewer-wall-label">
          <GalleryLabel {...currentLabel} />
        </div>
      </div>
    </main>
  );
};

export default Gallery;
